import { set } from 'mobx';
import { getRoot, type Instance, types } from 'mobx-state-tree';

import {
  type TrackingCurrentPage,
  TRACKING_CANDIDATE_DETAILS,
} from 'src/constants/pageTrackingData';

import {
  STATUS_CERTIFIED,
  STATUS_CUSTOM,
  STATUS_MATCH,
} from '../../constants/roleRequirementStatus';
import { JOB_APPLICATION_TAB } from '../../constants/tabs';
import type { IStore } from '../Store';
import {
  STATE_ERROR,
  STATE_LOADED,
  STATE_LOADING,
  stateTypeWithSummary,
} from '../state';

import AttachmentsStore from './AttachmentsStore';
import CandidateStore from './CandidateStore';
import JobApplicationSummary from './JobApplicationSummary';
import { MatchedQualities } from './MatchedQuality/MatchedQuality';
import Notes from './Notes';
import ProfileStore from './ProfileStore';
import ProspectEmailStore from './ProspectEmailStore';
import { Rating } from './Rating';
import RoleRequirement from './RoleRequirement';
import { Verification } from './VerificationStore/Verification';
import { BUCKETS } from './types';

const JobApplicationDetails = types
  .model('JobApplicationDetails', {
    candidateId: types.string,
    billableStatus: types.union(
      types.literal('BILLABLE'),
      types.literal('NOT_BILLABLE'),
      types.undefined, // FIXME: Graph returns null for this field, however we always call removeNullValueFromObject to remove null fields. The reason is not clear.
    ),
    seekCandidateId: types.optional(types.string, ''),
    position: types.optional(types.number, -1),
    mostRecentJobTitle: types.optional(types.string, ''),
    mostRecentCompanyName: types.optional(types.string, ''),
    mostRecentRoleMonths: types.optional(types.number, 0),
    appliedDate: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    phone: types.optional(types.string, ''),
    bucket: types.optional(
      types.enumeration<BUCKETS>('Buckets', Object.values(BUCKETS)),
      BUCKETS.UNKNOWN,
    ),
    roleRequirements: types.array(RoleRequirement),
    hasRRError: types.optional(types.boolean, false),
    state: stateTypeWithSummary,
    isChangingState: types.optional(types.boolean, false),
    isChangeStateError: types.optional(types.boolean, false),
    verifications: types.array(Verification),
    jobApplicationId: types.optional(types.string, ''),
    notes: types.optional(Notes, {}),
    prospectEmailStore: types.optional(ProspectEmailStore, {}),
    candidateStore: types.optional(CandidateStore, {}),
    attachmentsStore: types.optional(AttachmentsStore, {}),
    selectedTabInDetailView: types.optional(
      types.enumeration<JOB_APPLICATION_TAB>(
        'Tabs',
        Object.values(JOB_APPLICATION_TAB),
      ),
      JOB_APPLICATION_TAB.RESUME,
    ),
    rating: types.optional(Rating, {}),
    source: types.optional(types.number, 0),
    selected: types.optional(types.boolean, false),
    profile: types.maybeNull(ProfileStore),
    matchedQualities: types.optional(MatchedQualities, {}),
    referenceCheckStatus: types.maybeNull(
      types.union(
        types.model({
          status: types.literal('available'),
          finishedCount: types.number,
          totalCount: types.number,
        }),
        types.model({
          status: types.literal('requested'),
        }),
      ),
    ),
    verificationsCount: types.optional(types.number, 0),
  })
  .views((self) => ({
    get sortedMatchedQualities() {
      if (self.matchedQualities.qualities) {
        return self.matchedQualities.qualities.slice().sort((a, b) => {
          if (a.isTop === b.isTop) {
            if (a.relevanceScore === b.relevanceScore) {
              return b.displayLabel > a.displayLabel ? -1 : 1;
            }
            return b.relevanceScore < a.relevanceScore ? -1 : 1;
          }
          return a.isTop ? -1 : 1;
        });
      }
      return [];
    },
  }))
  .actions((self) => ({
    setReferenceCheckStatusRequested: () => {
      self.referenceCheckStatus = {
        status: 'requested',
      };
    },
  }));

const JobApplication = types
  .compose(JobApplicationDetails, JobApplicationSummary)
  .named('JobApplication')
  .views((self) => ({
    get rootStore(): IStore {
      return getRoot(self);
    },

    get isLoaded() {
      return self.state === STATE_LOADED;
    },

    get isLoading() {
      return self.state === STATE_LOADING;
    },

    get isError() {
      return self.state === STATE_ERROR;
    },

    get certifiedRoleRequirements() {
      return self.roleRequirements.filter(
        (rr) => rr.status === STATUS_CERTIFIED,
      );
    },

    get certifiedAnswersTotalCount() {
      // total count is the sum all RR's with a status of STATUS_CERTIFIED and
      // all answers that are displayed with verified tags

      const roleRequirementsWithVerifiedTags = self.roleRequirements.filter(
        (rr) => rr.showVerifiedTagInAnswers,
      );

      const verifiedTagCount = roleRequirementsWithVerifiedTags.reduce(
        (count, rr) => {
          const verifiedAnswerCount = rr.rrAnswers.filter(
            (answer) => answer.isVerified,
          ).length;
          return count + verifiedAnswerCount;
        },
        0,
      );

      return verifiedTagCount + this.certifiedRoleRequirements.length;
    },

    get attachments() {
      return self.attachmentsStore.attachmentList;
    },

    get noteAttachmentCount() {
      return (
        self.notes.noteCount +
        self.attachmentsStore.hirerUploadedDocuments.length
      );
    },

    get matchRoleRequirements() {
      return self.roleRequirements.filter(
        (rr) => rr.status === STATUS_MATCH || rr.status === STATUS_CERTIFIED,
      );
    },

    get notCustomRoleRequirements() {
      return self.roleRequirements.filter((rr) => rr.status !== STATUS_CUSTOM);
    },

    get customRoleRequirements() {
      return self.roleRequirements.filter((rr) => rr.status === STATUS_CUSTOM);
    },
  }))
  .views((self) => ({
    get isNotLoaded() {
      return !self.isLoaded;
    },

    get positionId() {
      // JH: this recurses up.. should simplify
      return self.jobApplicationStore.positionId;
    },

    get jobInformation() {
      return self.rootStore.jobStore.job.jobInformation;
    },
  }))
  .views((self) => ({
    get isInbox() {
      return self.bucket === BUCKETS.INBOX;
    },

    get shouldShowBadge() {
      return self.isUnread || self.certifiedRoleRequirements.length > 0;
    },

    get baseTrackingAttributes(): {
      candidateId: string;
      currentPage: TrackingCurrentPage;
    } {
      return {
        candidateId: self.candidateId,
        currentPage: TRACKING_CANDIDATE_DETAILS,
      };
    },
  }))
  .actions((self) => {
    const updateDetails = (application: Record<string, any>) => {
      set(self, application);
      self.state = STATE_LOADED;
    };

    const updateSelected = (selected: boolean): void => {
      self.selected = selected;
    };

    const toggleSelect = (): void => {
      self.jobApplicationStore.updateSelectAllClickedState(false);
      updateSelected(!self.selected);
    };

    const selectTabInDetailView = (tab: JOB_APPLICATION_TAB) => {
      self.selectedTabInDetailView = tab;
    };

    const setIsChangingState = (state: boolean) => {
      self.isChangingState = state;
    };

    const setStatus = (status: BUCKETS) => {
      self.bucket = status;
    };

    return {
      updateDetails,
      selectTabInDetailView,
      updateSelected,
      toggleSelect,
      setIsChangingState,
      setStatus,
    };
  });

export interface IJobApplication extends Instance<typeof JobApplication> {}

export default JobApplication;
